/** Date formatting and deadline countdowns. */

const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;

export function parseDate(value) {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : new Date(ms);
}

/** "Aug 15 – 16, 2026" / "Dec 28, 2026 – Jan 3, 2027" */
export function formatDateRange(startIso, endIso) {
  const start = parseDate(startIso);
  const end = parseDate(endIso);
  if (!start) return 'Dates TBA';

  const sameYear = end && start.getFullYear() === end.getFullYear();
  const sameMonth = sameYear && start.getMonth() === end.getMonth();
  const sameDay = sameMonth && start.getDate() === end.getDate();

  const month = { month: 'short', timeZone: 'UTC' };
  const monthDay = { month: 'short', day: 'numeric', timeZone: 'UTC' };
  const full = { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' };

  if (!end || sameDay) return start.toLocaleDateString(undefined, full);
  if (sameMonth) {
    return `${start.toLocaleDateString(undefined, monthDay)} – ${end.getUTCDate()}, ${end.getUTCFullYear()}`;
  }
  if (sameYear) {
    return `${start.toLocaleDateString(undefined, monthDay)} – ${end.toLocaleDateString(undefined, full)}`;
  }
  return `${start.toLocaleDateString(undefined, full)} – ${end.toLocaleDateString(undefined, full)}`;
}

/**
 * Countdown to a deadline.
 * Returns null when there's no deadline, so the card can omit the row entirely
 * rather than render an empty or misleading one.
 */
export function deadlineCountdown(deadlineIso, now = Date.now()) {
  const deadline = parseDate(deadlineIso);
  if (!deadline) return null;

  const remaining = deadline.getTime() - now;
  if (remaining <= 0) return { text: 'Closed', urgency: 'closed', remaining };

  const days = Math.floor(remaining / DAY_MS);
  const hours = Math.floor((remaining % DAY_MS) / HOUR_MS);

  let text;
  if (days === 0) text = hours <= 1 ? 'Under an hour left' : `${hours}h left`;
  else if (days === 1) text = `1 day left`;
  else if (days < 30) text = `${days} days left`;
  else if (days < 60) text = `1 month left`;
  else text = `${Math.round(days / 30)} months left`;

  const urgency = days <= 2 ? 'urgent' : days <= 7 ? 'soon' : 'normal';
  return { text, urgency, remaining };
}

/** Has the registration deadline passed? Records without a deadline are never hidden. */
export function deadlinePassed(hackathon, now = Date.now()) {
  const deadline = parseDate(hackathon.registration_deadline);
  return deadline ? deadline.getTime() < now : false;
}

/** Has the event itself finished? */
export function eventFinished(hackathon, now = Date.now()) {
  const end = parseDate(hackathon.end_date || hackathon.start_date);
  return end ? end.getTime() < now : false;
}

/** ISO yyyy-mm-dd, for <input type="date"> values. */
export function toDateInputValue(date) {
  return date.toISOString().slice(0, 10);
}
